import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─── Types ──────────────────────────────────────────────────────────────

export interface JobRunRow {
  id: string;
  job_name: string;
  run_id: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  rows_processed: number;
  rows_skipped: number;
  rows_failed: number;
  success: boolean;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export type JobRunStatus = 'succeeded' | 'failed' | 'partial';

export interface JobRunsSummary {
  runsToday: number;
  failed: number;
  partial: number;
  avgDurationSeconds: number;
  totalItemsFailed: number;
}

export interface JobRunsData {
  rows: JobRunRow[];
  summary: JobRunsSummary;
  jobNames: string[];
}

// ─── Pure helpers (exported for testing) ────────────────────────────────

export function classifyRunStatus(row: JobRunRow): JobRunStatus {
  if (!row.success) return 'failed';
  if (row.rows_failed > 0) return 'partial';
  return 'succeeded';
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--';
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

export function computeSummary(rows: JobRunRow[]): JobRunsSummary {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayIso = todayStart.toISOString();

  const todayRuns = rows.filter((r) => r.started_at >= todayIso);
  const failed = todayRuns.filter((r) => !r.success);
  const partial = todayRuns.filter((r) => r.success && r.rows_failed > 0);

  const durations = todayRuns
    .map((r) => r.duration_seconds)
    .filter((d): d is number => d != null);
  const avgDuration = durations.length > 0
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : 0;

  const totalItemsFailed = todayRuns.reduce((sum, r) => sum + r.rows_failed, 0);

  return {
    runsToday: todayRuns.length,
    failed: failed.length,
    partial: partial.length,
    avgDurationSeconds: avgDuration,
    totalItemsFailed,
  };
}

// ─── Fetch ──────────────────────────────────────────────────────────────

async function fetchJobRuns(): Promise<JobRunsData> {
  // Fetch last 7 days of runs (reasonable window for admin observability)
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('cron_run_health')
    .select('*')
    .gte('started_at', sevenDaysAgo)
    .order('started_at', { ascending: false })
    .limit(500);

  if (error) throw error;

  const rows = (data ?? []) as JobRunRow[];
  const summary = computeSummary(rows);
  const jobNames = [...new Set(rows.map((r) => r.job_name))].sort();

  return { rows, summary, jobNames };
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAdminJobRuns(enabled: boolean, refetchInterval: number | false) {
  return useQuery({
    queryKey: ['admin-job-runs'],
    queryFn: fetchJobRuns,
    enabled,
    refetchInterval,
    staleTime: 30_000,
  });
}
